import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const SECTIONS = [
  {
    id: 'eligibility',
    title: '1. Eligibility & Account Registration',
    body: [
      'Aloha Amino Research Inc. ("Aloha Amino", "we", "us") sells products exclusively to qualified researchers and laboratory providers. By creating an account and placing an order, you represent and warrant that you are: (a) a licensed researcher, postdoctoral fellow, or graduate student affiliated with a recognized research institution; (b) a qualified laboratory provider or medical professional; or (c) otherwise authorised to purchase research chemicals under applicable law.',
      'You must be at least 21 years of age and provide accurate information during registration. Aloha Amino reserves the right to verify your eligibility at any time and to suspend or terminate accounts that do not meet these criteria.',
    ],
  },
  {
    id: 'research-use',
    title: '2. Research Use Only — Prohibited Uses',
    body: [
      'ALL products sold by Aloha Amino are intended solely for in-vitro and ex-vivo laboratory research purposes. They are NOT intended for human or veterinary consumption, diagnosis, treatment, or prevention of any disease or condition. They are not drugs, foods, cosmetics, or medical devices.',
      'You must NOT use, administer, or distribute any Aloha Amino product in or on any human or animal. You must NOT resell products for any consumer or clinical purpose. You must NOT make any therapeutic, medical, or health claim in connection with Aloha Amino products when referring others or operating as an affiliate. Violation of this section will result in immediate account termination and may be reported to applicable regulatory authorities.',
    ],
  },
  {
    id: 'orders',
    title: '3. Orders, Pricing & Payment',
    body: [
      'All prices are listed in United States dollars (USD) and are exclusive of applicable taxes. Aloha Amino reserves the right to change prices at any time without notice. Your order constitutes an offer to purchase; acceptance occurs when Aloha Amino sends an order confirmation email.',
      'Payment is processed securely via Stripe. By placing an order you authorise the charge to your selected payment method. Shipping is payable in full on every order. Aloha Amino is not responsible for foreign transaction fees charged by your card issuer.',
    ],
  },
  {
    id: 'shipping',
    title: '4. Shipping & Delivery',
    body: [
      'Aloha Amino ships domestically only. Estimated delivery times are provided at checkout and are not guaranteed. Risk of loss passes to you upon handover to the carrier. If your order is lost or damaged in transit, contact support@alohaamino.example.com within 7 calendar days of the expected delivery date.',
    ],
  },
  {
    id: 'returns',
    title: '5. Returns & Refunds',
    body: [
      'Due to the perishable and regulated nature of research chemicals, all sales are final unless the product is defective or incorrectly shipped. If you receive a defective or incorrect product, contact us within 5 business days of receipt with your order number and photographs of the product and packaging. Approved refunds are credited to your original payment method within 10 business days.',
    ],
  },
  {
    id: 'ip',
    title: '6. Intellectual Property',
    body: [
      'All content on the Aloha Amino website — including text, graphics, logos, and product descriptions — is the property of Aloha Amino Research Inc. or its content suppliers and is protected by applicable copyright law. You may not reproduce, distribute, or create derivative works without written permission.',
    ],
  },
  {
    id: 'liability',
    title: '7. Limitation of Liability',
    body: [
      'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ALOHA AMINO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF OUR PRODUCTS OR SERVICES. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE ORDER GIVING RISE TO THAT CLAIM.',
      'Products are sold "as-is" for research purposes. Aloha Amino makes no representations or warranties — express or implied — regarding the fitness of any product for any particular research application.',
    ],
  },
  {
    id: 'governing-law',
    title: '8. Governing Law & Disputes',
    body: [
      'These Terms are governed by applicable law, without regard to conflict-of-law principles. Any disputes shall be resolved in a court of competent jurisdiction.',
    ],
  },
  {
    id: 'changes',
    title: '9. Changes to These Terms',
    body: [
      'Aloha Amino may update these Terms at any time. Material changes will be communicated by email and/or a notice on the website. Continued use of the site after the effective date constitutes acceptance of the revised Terms.',
      'Last updated: January 2025.',
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <section className="bg-tea-700 text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Terms of Service</h1>
          <p className="mt-3 text-tea-200">Last updated January 2025 · Aloha Amino Research Inc.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 flex gap-12">
        {/* Sidebar TOC — hidden on mobile */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-sm text-gray-500 hover:text-tea-700 transition-colors py-1 leading-tight"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10 text-amber-900 text-sm leading-relaxed">
            <strong>⚠️ Important:</strong> All Aloha Amino products are for in-vitro laboratory research use only. They are not intended for human or veterinary use. By purchasing you confirm you meet the eligibility requirements in Section 1.
          </div>

          <div className="prose prose-gray max-w-none">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="mb-10 scroll-mt-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{s.title}</h2>
                {s.body.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-4">{para}</p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <Link to="/privacy" className="text-tea-700 font-medium hover:underline text-sm">Privacy Policy</Link>
            <Link to="/compliance" className="text-tea-700 font-medium hover:underline text-sm">Research Use Disclosure</Link>
            <a href="mailto:support@alohaamino.example.com" className="text-tea-700 font-medium hover:underline text-sm">Contact Us</a>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
